import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Search, FolderKanban, Upload, FileText, X } from 'lucide-react';
import { useStore } from '../store/useStore';
import { apiClient } from '../lib/apiClient';
import { formatDate, formatCurrency, formatFileSize, isAdminRole } from '../lib/utils';
import { ProjectBadge } from '../components/Badge';
import ProgressBar from '../components/ProgressBar';
import BudgetSpent from '../components/BudgetSpent';
import { useClickUpStatus } from '../lib/clickupStatus';
import Modal from '../components/Modal';
import AiReviewModal from '../components/AiReviewModal';
import type { Project, ProjectStatus } from '../types';

// ── Stage generation from offer timeframe ────────────────────────────────────
function generateStages(projectId: string, startDate: string, endDate: string) {
  const toStr = (d: Date) => d.toISOString().split('T')[0];
  const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86400000);

  const start = new Date(startDate);
  const end = new Date(endDate);
  const total = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000));

  // Proportional split: 10% kickoff | 25% design | 50% development | 15% launch
  const splits = [0.10, 0.25, 0.50, 0.15];
  const templates = [
    { title: 'Projekti käivitamine & materjalide kogumine', description: 'Projekti seadistamine, kliendi materjalide, sisu ja brändiraamatu kogumine.' },
    { title: 'Disain',                                      description: 'UX/UI kujundus. Korrektuurivoorud kliendiga.' },
    { title: 'Arendustöö',                                  description: 'Frontend ja backend arendus, integratsioonid.' },
    { title: 'Testimine & käivitamine',                     description: 'QA testimine, SEO seadistus, live keskkonda viimine, kliendi koolitus.' },
  ];

  const stages = [];
  let cursor = new Date(start);
  for (let i = 0; i < templates.length; i++) {
    const days = Math.round(total * splits[i]);
    const stageEnd = i === templates.length - 1 ? end : addDays(cursor, days - 1);
    stages.push({
      projectId,
      title: templates[i].title,
      description: templates[i].description,
      order: i + 1,
      plannedStart: toStr(cursor),
      plannedEnd: toStr(stageEnd),
      completionPercent: 0,
      status: 'not_started' as const,
      notes: '',
      blockerNote: '',
    });
    cursor = addDays(stageEnd, 1);
  }
  return stages;
}

function generateInvoices(projectId: string, stageIds: string[], value: number, startDate: string, endDate: string) {
  return [
    {
      projectId,
      relatedStageId: stageIds[0] ?? '',
      title: 'Ettemaks (50%) — töö alustamine',
      amount: Math.round(value * 0.5),
      percentage: 50,
      plannedSendDate: startDate,
      sentDate: '', paidDate: '',
      status: 'not_ready' as const,
      notes: '',
    },
    {
      projectId,
      relatedStageId: stageIds[stageIds.length - 1] ?? '',
      title: 'Lõppmakse (50%) — töö lõpetamine',
      amount: Math.round(value * 0.5),
      percentage: 50,
      plannedSendDate: endDate,
      sentDate: '', paidDate: '',
      status: 'not_ready' as const,
      notes: '',
    },
  ];
}

function FileUploadSlot({
  label,
  file,
  onFile,
  onClear,
}: {
  label: string;
  file: File | null;
  onFile: (f: File) => void;
  onClear: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {file ? (
        <div className="flex items-center gap-2 px-3 py-2 bg-brand-50 border border-brand-200 rounded-lg">
          <FileText size={14} className="text-brand-500 shrink-0" />
          <span className="text-xs text-gray-700 truncate flex-1">{file.name}</span>
          <span className="text-xs text-gray-400 shrink-0">{formatFileSize(file.size)}</span>
          <button onClick={onClear} className="text-gray-400 hover:text-red-500 shrink-0">
            <X size={13} />
          </button>
        </div>
      ) : (
        <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-brand-400 hover:bg-brand-50 transition-colors">
          <Upload size={14} className="text-gray-400" />
          <span className="text-xs text-gray-400">Upload {label.toLowerCase()} (PDF / DOCX)</span>
          <input
            ref={ref}
            type="file"
            className="hidden"
            accept=".pdf,.docx"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
          />
        </label>
      )}
    </div>
  );
}

function ProjectForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: Partial<Project>;
  onSave: (data: Omit<Project, 'id' | 'createdAt' | 'overallCompletion'>, offerFile: File | null, contractFile: File | null) => void;
  onCancel: () => void;
}) {
  const clients = useStore((s) => s.clients);
  const users = useStore((s) => s.users);
  const currentUser = useStore((s) => s.currentUser);

  const today = new Date().toISOString().split('T')[0];
  const addDays = (dateStr: string, n: number) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + n);
    return d.toISOString().split('T')[0];
  };
  // Default owner/PM to the current user where their role allows it.
  const meAsSales = currentUser && ['sales', 'sales_pm', 'admin', 'super_admin'].includes(currentUser.role) ? currentUser.id : '';
  const meAsPm = currentUser && ['pm', 'sales_pm', 'admin', 'super_admin'].includes(currentUser.role) ? currentUser.id : '';

  const [form, setForm] = useState({
    clientId: initial?.clientId ?? '',
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    value: initial?.value ?? 0,
    currency: initial?.currency ?? 'EUR',
    salesOwnerId: initial?.salesOwnerId ?? meAsSales,
    projectManagerId: initial?.projectManagerId ?? meAsPm,
    startDate: initial?.startDate ? initial.startDate.split('T')[0] : today,
    endDate: initial?.endDate ? initial.endDate.split('T')[0] : addDays(today, 60),
    status: (initial?.status ?? 'active') as ProjectStatus,
  });
  const [offerFile, setOfferFile] = useState<File | null>(null);
  const [contractFile, setContractFile] = useState<File | null>(null);

  const set = (key: keyof typeof form, val: string | number) =>
    setForm((f) => ({ ...f, [key]: val }));

  const salesUsers = users.filter((u) => u.role === 'sales' || u.role === 'sales_pm' || u.role === 'admin' || u.role === 'super_admin');
  const pmUsers = users.filter((u) => u.role === 'pm' || u.role === 'sales_pm' || u.role === 'admin' || u.role === 'super_admin');

  const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500';

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Client *</label>
        <select value={form.clientId} onChange={(e) => set('clientId', e.target.value)} className={inputCls}>
          <option value="">Select client...</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.companyName}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Project Name *</label>
        <input value={form.name} onChange={(e) => set('name', e.target.value)} className={inputCls} placeholder="e.g. Website Redesign" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Value</label>
          <input type="number" value={form.value} onChange={(e) => set('value', Number(e.target.value))} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
          <select value={form.currency} onChange={(e) => set('currency', e.target.value)} className={inputCls}>
            <option>EUR</option><option>USD</option><option>GBP</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Sales Owner</label>
          <select value={form.salesOwnerId} onChange={(e) => set('salesOwnerId', e.target.value)} className={inputCls}>
            <option value="">Select...</option>
            {salesUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Project Manager</label>
          <select value={form.projectManagerId} onChange={(e) => set('projectManagerId', e.target.value)} className={inputCls}>
            <option value="">Select...</option>
            {pmUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
          <input type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
          <input type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} className={inputCls} />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
        <select value={form.status} onChange={(e) => set('status', e.target.value as ProjectStatus)} className={inputCls}>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="on_hold">On Hold</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="border-t border-gray-100 pt-4 space-y-3">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Documents (optional)</p>
        <div className="grid grid-cols-2 gap-3">
          <FileUploadSlot label="Offer" file={offerFile} onFile={setOfferFile} onClear={() => setOfferFile(null)} />
          <FileUploadSlot label="Contract" file={contractFile} onFile={setContractFile} onClear={() => setContractFile(null)} />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        {(() => {
          const missing = [
            !form.clientId && 'client',
            !form.name.trim() && 'name',
            !form.salesOwnerId && 'sales owner',
            !form.projectManagerId && 'project manager',
            !form.startDate && 'start date',
            !form.endDate && 'end date',
          ].filter(Boolean) as string[];
          const datesInvalid = form.startDate && form.endDate && form.endDate < form.startDate;
          const canSave = missing.length === 0 && !datesInvalid;
          return (
            <>
              {missing.length > 0 && (
                <span className="text-xs text-gray-400 mr-auto">Missing: {missing.join(', ')}</span>
              )}
              {datesInvalid && (
                <span className="text-xs text-red-500 mr-auto">End date must be after start date</span>
              )}
              <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:text-gray-900">
                Cancel
              </button>
              <button
                onClick={() => canSave && onSave(form, offerFile, contractFile)}
                disabled={!canSave}
                className="px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium disabled:opacity-50"
              >
                Save Project
              </button>
            </>
          );
        })()}
      </div>
    </div>
  );
}

export default function Projects() {
  const currentUser = useStore((s) => s.currentUser);
  const projects = useStore((s) => s.projects);
  const clients = useStore((s) => s.clients);
  const users = useStore((s) => s.users);
  const addProject = useStore((s) => s.addProject);
  const navigate = useNavigate();
  const location = useLocation();
  const { status: clickup } = useClickUpStatus();

  // Arriving from a client page with "New Project" — open the modal with that
  // client preselected.
  const presetClientId = (location.state as { newProjectClientId?: string } | null)?.newProjectClientId;

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [pmFilter, setPmFilter] = useState<string>('all');
  const [showAdd, setShowAdd] = useState(!!presetClientId);
  const [aiDocQueue, setAiDocQueue] = useState<{ docId: string; projectId: string; projectName: string } | null>(null);

  const visibleProjects = projects.filter((p) => {
    if (isAdminRole(currentUser?.role)) return true;
    if (currentUser?.role === 'sales') return p.salesOwnerId === currentUser.id;
    if (currentUser?.role === 'pm') return p.projectManagerId === currentUser.id;
    if (currentUser?.role === 'sales_pm') return p.salesOwnerId === currentUser.id || p.projectManagerId === currentUser.id;
    return false;
  });

  const filtered = visibleProjects.filter((p) => {
    const client = clients.find((c) => c.id === p.clientId);
    const matchSearch =
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      client?.companyName.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || p.status === statusFilter;
    const matchPm = pmFilter === 'all' || p.projectManagerId === pmFilter;
    return matchSearch && matchStatus && matchPm;
  });

  const pmUsers = users.filter((u) =>
    visibleProjects.some((p) => p.projectManagerId === u.id)
  );

  const getClient = (id: string) => clients.find((c) => c.id === id);
  const getUser = (id: string) => users.find((u) => u.id === id);

  const STATUS_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'draft', label: 'Draft' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="text-gray-500 text-sm mt-0.5">{filtered.length} projects shown</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600"
        >
          <Plus size={15} /> New Project
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        {pmUsers.length > 1 && (
          <select
            value={pmFilter}
            onChange={(e) => setPmFilter(e.target.value)}
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 text-gray-700"
          >
            <option value="all">All PMs</option>
            {pmUsers.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        )}
        <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                statusFilter === opt.value
                  ? 'bg-brand-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Projects grid */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <FolderKanban size={40} className="text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No projects match your filters</p>
          </div>
        )}
        {filtered.map((project) => {
          const client = getClient(project.clientId);
          const pm = getUser(project.projectManagerId);
          const sales = getUser(project.salesOwnerId);
          return (
            <button
              key={project.id}
              onClick={() => navigate(`/projects/${project.id}`)}
              className="w-full bg-white rounded-xl border border-gray-100 p-5 text-left hover:shadow-md transition-shadow group"
            >
              <div className="flex items-center gap-5">
                <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
                  <FolderKanban size={18} className="text-brand-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-gray-900 truncate">{project.name}</span>
                    <ProjectBadge status={project.status} />
                  </div>
                  <p className="text-xs text-gray-400">
                    {client?.companyName} · Sales: {sales?.name} · PM: {pm?.name}
                  </p>
                  <div className="mt-2.5 grid grid-cols-2 gap-5 max-w-lg">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Completion</p>
                      <ProgressBar value={project.overallCompletion} showLabel />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Budget spent</p>
                      <BudgetSpent project={project} hourlyCost={clickup?.hourlyCost ?? null} />
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0 hidden sm:block">
                  <div className="font-bold text-gray-900">{formatCurrency(project.value, project.currency)}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {formatDate(project.startDate)} → {formatDate(project.endDate)}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="New Project" size="lg">
        <ProjectForm
          initial={presetClientId ? { clientId: presetClientId } : undefined}
          onSave={async (data, offerFile, contractFile) => {
            await addProject(data);
            await useStore.getState().refetchProjects();
            const newProject = useStore.getState().projects
              .filter((p) => p.name === data.name && p.clientId === data.clientId)
              .slice(-1)[0];
            setShowAdd(false);
            if (!newProject) return;

            // Upload documents to server (triggers AI extraction automatically)
            const filesToUpload: { file: File; type: 'offer' | 'contract' }[] = [];
            if (offerFile) filesToUpload.push({ file: offerFile, type: 'offer' });
            if (contractFile) filesToUpload.push({ file: contractFile, type: 'contract' });

            if (filesToUpload.length > 0) {
              // Upload and queue for AI review (one at a time, last one gets the modal)
              let lastDocId: string | null = null;
              for (const { file, type } of filesToUpload) {
                try {
                  const fd = new FormData();
                  fd.append('file', file);
                  fd.append('projectId', newProject.id);
                  fd.append('type', type);
                  const resp = await apiClient.upload<{ document: { id: string } }>('/documents', fd);
                  lastDocId = resp.document.id;
                } catch {
                  // upload failed silently; user can upload from Documents tab later
                }
              }
              if (lastDocId) {
                await useStore.getState().refetchProjects();
                setAiDocQueue({ docId: lastDocId, projectId: newProject.id, projectName: newProject.name });
              }
            }
          }}
          onCancel={() => setShowAdd(false)}
        />
      </Modal>

      {/* AI review modal after project creation upload */}
      {aiDocQueue && (
        <AiReviewModal
          open={!!aiDocQueue}
          documentId={aiDocQueue.docId}
          projectName={aiDocQueue.projectName}
          currentProject={useStore.getState().projects.find((p) => p.id === aiDocQueue.projectId)}
          onClose={() => setAiDocQueue(null)}
          onApplied={async () => {
            await useStore.getState().refetchProjects();
            setAiDocQueue(null);
            navigate(`/projects/${aiDocQueue.projectId}`);
          }}
        />
      )}
    </div>
  );
}
